/**
/**
 * Amani Store - Wishlist JS Utility & Controller
 * Implements client-side LocalStorage wishlist, click events, and server sync.
 */

const WISHLIST_KEY = 'shop_wishlist_v1';

const Wishlist = {
  isSyncing: false,

  // Get current wishlist product ID strings from LocalStorage
  getWishlist() {
    try {
      const data = localStorage.getItem(WISHLIST_KEY);
      return data ? JSON.parse(data).map(String) : [];
    } catch (e) {
      console.error('Error reading wishlist from localStorage:', e);
      return [];
    }
  },

  // Add a product ID to the client-side wishlist
  addToWishlist(id) {
    if (!id) return;
    const idStr = String(id);
    const wishlist = this.getWishlist();
    if (!wishlist.includes(idStr)) {
      wishlist.push(idStr);
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
      } catch (e) {
        console.error('Error writing wishlist to localStorage:', e);
      }
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist, action: 'add', id: idStr } }));
    }
  },

  // Remove a product ID from the client-side wishlist
  removeFromWishlist(id) {
    if (!id) return;
    const idStr = String(id);
    let wishlist = this.getWishlist();
    if (wishlist.includes(idStr)) {
      wishlist = wishlist.filter(item => item !== idStr);
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
      } catch (e) {
        console.error('Error writing wishlist to localStorage:', e);
      }
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist, action: 'remove', id: idStr } }));
    }
  },

  // Check if a product ID exists in either local or server-side wishlist
  isInWishlist(id) {
    if (!id) return false;
    const idStr = String(id);
    const localList = this.getWishlist();
    const customerList = window.customerWishlist || [];
    return localList.includes(idStr) || customerList.includes(idStr);
  },

  // Synchronize client wishlist items to Shopify Customer Metafields via App Proxy
  async syncWithServer(action = {}) {
    if (this.isSyncing) return;
    this.isSyncing = true;

    const localItems = this.getWishlist();
    const payload = {
      ...action,
      local_items: localItems
    };

    try {
      const response = await fetch('/apps/wishlist/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json().catch(() => null);

        // Update local memory of synced customer wishlist
        if (data && Array.isArray(data.wishlist)) {
          window.customerWishlist = data.wishlist.map(String);
        } else {
          // If no response body, manually merge based on action success
          if (localItems.length > 0) {
            window.customerWishlist = [...new Set([...(window.customerWishlist || []), ...localItems])];
          }
          if (action.add_id) {
            window.customerWishlist = [...new Set([...(window.customerWishlist || []), String(action.add_id)])];
          }
          if (action.remove_id) {
            window.customerWishlist = (window.customerWishlist || []).filter(item => item !== String(action.remove_id));
          }
        }

        // Only clear the client-side LocalStorage key AFTER receiving a successful 200 OK
        if (localItems.length > 0) {
          localStorage.removeItem(WISHLIST_KEY);
        }

        document.dispatchEvent(new CustomEvent('wishlist:synced', { detail: { wishlist: window.customerWishlist } }));
        this.updateHearts();
      } else {
        console.warn('Wishlist synchronization returned non-200 status:', response.status);
      }
    } catch (error) {
      console.error('Error syncing wishlist with server:', error);
    } finally {
      this.isSyncing = false;
    }
  },

  // Update visual state (is-active class) of all wishlist buttons on the page
  updateHearts() {
    const buttons = document.querySelectorAll('.wishlist-toggle-btn, .buy-box-like-btn, .mobile-like-btn, .product-card-like-btn');
    buttons.forEach(btn => {
      const id = btn.getAttribute('data-product-id');
      if (id) {
        if (this.isInWishlist(id)) {
          btn.classList.add('is-active');
        } else {
          btn.classList.remove('is-active');
        }
      }
    });
  },

  // Initial setup: run sync if customer is logged in, attach event listeners, etc.
  init() {
    // Check if customer is logged in and sync
    if (window.customerLoggedIn) {
      const localItems = this.getWishlist();
      if (localItems.length > 0) {
        this.syncWithServer();
      }
    }

    this.updateHearts();

    // Listen for dynamically added elements (mutation observer or custom events)
    document.addEventListener('wishlist:updated', () => this.updateHearts());
    document.addEventListener('wishlist:synced', () => this.updateHearts());
  }
};

// Global Event Delegation for Wishlist Buttons click handling
document.addEventListener('click', (event) => {
  const btn = event.target.closest('.wishlist-toggle-btn, .buy-box-like-btn, .mobile-like-btn, .product-card-like-btn');
  if (!btn) return;

  event.preventDefault();
  event.stopPropagation();

  const id = btn.getAttribute('data-product-id');
  if (!id) return;

  const isActive = Wishlist.isInWishlist(id);

  if (isActive) {
    Wishlist.removeFromWishlist(id);
    if (window.customerLoggedIn) {
      Wishlist.syncWithServer({ remove_id: id });
    }
  } else {
    Wishlist.addToWishlist(id);
    if (window.customerLoggedIn) {
      Wishlist.syncWithServer({ add_id: id });
    }
  }

  Wishlist.updateHearts();
});

// Run init on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => Wishlist.init());
} else {
  Wishlist.init();
}

window.Wishlist = Wishlist;
export default Wishlist;
